import { ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageModule } from 'primeng/message';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { Job, JobParameter } from '../../../models/job';
import { JobService } from '../../../core/services/api/job.service';
import { StreamChatComponent } from './stream-chat.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-job-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    CheckboxModule,
    RadioButtonModule,
    MessageModule,
    StreamChatComponent,
    TranslocoPipe,
  ],
  templateUrl: './job-detail.component.html',
  styleUrls: ['./job-detail.component.scss'],
})
export class JobDetailComponent implements OnInit {
  job: Job | null = null;
  parameters: JobParameter[] = [];
  isLoading: boolean = false;
  isExecuting: boolean = false;
  jobForm: FormGroup;
  executionResult: string = '';
  showExecutionResult: boolean = false;
  isTyping: boolean = false;
  displayText: string = '';
  @ViewChild(StreamChatComponent) streamChatComponent!: StreamChatComponent;

  apiUrl = 'http://192.168.101.58/v1';
  payload: any = {
    inputs: {},
    response_mode: 'streaming',
    user: 'user-123',
  };
  token = 'Bearer ';
  appKey = '';

  private jobService = inject(JobService);
  private messageService = inject(MessageService);
  private formBuilder = inject(FormBuilder);
  private translocoService = inject(TranslocoService);
  public ref = inject(DynamicDialogRef);
  public config = inject(DynamicDialogConfig);
  response: string = '';
  cdr = inject(ChangeDetectorRef);

  constructor() {
    this.jobForm = this.formBuilder.group({});
    this.appKey = this.config.data.job.uuid;
    this.token = `Bearer ${this.appKey}`;
  }

  ngOnInit(): void {
    this.loadJobParameters();
  }

  async loadJobParameters(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await this.jobService.getJobParameters(this.appKey);
      const userInputForm = response.user_input_form || [];

      // Map the response to correct array format
      this.parameters = userInputForm.map((item: any) => {
        // Get the first key (parameter type) and extract the parameter data
        const paramType = Object.keys(item)[0];
        const paramData = item[paramType];

        return {
          label: paramData.label,
          type: paramData.type,
          required: paramData.required,
          variable: paramData.variable,
          max_length: paramData.max_length,
          options: paramData.options || [],
          description: paramData.description,
          default_value: paramData.default_value,
          value: paramData.value,
        };
      });

      this.buildForm();
    } catch (error) {
      console.error('Error loading job parameters:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('common.toast.error'),
        detail: this.translocoService.translate('jobDetailDialog.notifications.jobNotFound'),
      });
    } finally {
      this.isLoading = false;
    }
  }

  private buildForm(): void {
    const formControls: { [key: string]: any } = {};

    this.parameters.forEach((param) => {
      const validators = [];
      if (param.required) {
        validators.push(Validators.required);
      }
      if (param.max_length) {
        validators.push(Validators.maxLength(param.max_length));
      }

      formControls[param.variable] = ['', validators];
    });

    this.jobForm = this.formBuilder.group(formControls);
  }

  async executeJob(): Promise<void> {
    if (!this.validateParameters()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
      });
      return;
    }
    this.payload.inputs = this.jobForm.value;
    this.streamChatComponent.startStream();
  }

  validateParameters(): boolean {
    if (this.parameters.length === 0) {
      return true; // No parameters to validate
    }

    return this.jobForm.valid;
  }

  onCancel(): void {
    this.ref.close();
  }

  getRequiredFieldsCount(): number {
    return this.parameters.filter((p) => p.required).length;
  }
}
